import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Calendar, ChevronLeft, ChevronRight, RefreshCw, FileText, User, Clock, ArrowLeft, Copy, Check, AlertTriangle } from 'lucide-react'
import { AdminService } from '../../services/adminService'
import { format } from 'date-fns'
import { getAssignmentLogPresentation } from '../../utils/pharmacistAssignmentLog'

const CopyableId = ({ id, label }) => {
    const [copied, setCopied] = useState(false)
    const handleCopy = () => {
        if (!id) return;
        navigator.clipboard.writeText(id)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }
    if (!id) return <span>-</span>
    return (
        <div className="flex items-center gap-1 group">
            <span title={id} className="text-gray-900 font-medium">
                {id.substring(0, 8)}...
            </span>
            <button onClick={handleCopy} className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors" title={`Copy ${label}`}>
                {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3 opacity-0 xl:opacity-100 group-hover:opacity-100 transition-opacity" />}
            </button>
        </div>
    )
}

const AssignedByInfo = ({ log, adminUserMap }) => {
    const presentation = getAssignmentLogPresentation(log, adminUserMap)

    if (!presentation.byLabel) return null

    const labelColorClass = presentation.sourceKind === 'automated'
        ? 'text-purple-600'
        : presentation.isMissingAdminAttribution
            ? 'text-amber-700'
            : 'text-gray-700'

    return (
        <div className="text-xs text-gray-500 mt-1 flex flex-col gap-0.5">
            <span className="flex items-center gap-1">
                <span className="opacity-70">By:</span>
                <span className={`font-medium ${labelColorClass}`}>
                    {presentation.byLabel}
                </span>
            </span>
            {presentation.isMissingAdminAttribution && (
                <span className="inline-flex items-center gap-1 text-[11px] text-amber-700">
                    <AlertTriangle className="w-3 h-3" />
                    Missing admin UUID on saved log row
                </span>
            )}
        </div>
    )
}


const PharmacistAssignmentLogs = () => {
    const navigate = useNavigate()
    const [logs, setLogs] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [pharmacists, setPharmacists] = useState([])
    // Map: admin UUID -> username for resolving assigned_by / reassigned_by
    const [adminUserMap, setAdminUserMap] = useState({})

    // Pagination
    const [currentPage, setCurrentPage] = useState(1)
    const [totalPages, setTotalPages] = useState(1)
    const [totalRecords, setTotalRecords] = useState(0)
    const [currentConsultationCount, setCurrentConsultationCount] = useState(0)
    const [assignmentComparison, setAssignmentComparison] = useState(null)
    const [ownershipScope, setOwnershipScope] = useState('all')
    const pageSize = 20

    // Filters
    const [filters, setFilters] = useState({
        userSearch: '',
        pharmacistUsername: 'all',
        pharmacistType: 'all',
        assignmentType: 'all',
        status: 'all',
        outcome: 'all',
        dateField: 'assigned_at',
        // Default to today
        dateRange: {
            from: new Date(new Date().setHours(0, 0, 0, 0)).toISOString(),
            to: new Date(new Date().setHours(23, 59, 59, 999)).toISOString()
        }
    })

    // Custom date picker state
    const [customDateFrom, setCustomDateFrom] = useState(new Date().toISOString().split('T')[0])
    const [customDateTo, setCustomDateTo] = useState(new Date().toISOString().split('T')[0])
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false)

    // Debounced search
    const [searchQuery, setSearchQuery] = useState('')

    useEffect(() => {
        const timer = setTimeout(() => {
            setFilters(prev => ({ ...prev, userSearch: searchQuery }))
            setCurrentPage(1) // Reset to first page on search
        }, 500)
        return () => clearTimeout(timer)
    }, [searchQuery])

    // Load pharmacists for dropdown + build admin UUID->username map
    useEffect(() => {
        const loadPharmacists = async () => {
            const response = await AdminService.getAllPharmacists()
            if (response && response.data) {
                setPharmacists(response.data)
            }
        }
        const loadAdminUsers = async () => {
            const response = await AdminService.getAllAdminUsers()
            if (response && response.data) {
                const map = {}
                response.data.forEach(u => { map[u.id] = u.username })
                setAdminUserMap(map)
            }
        }
        loadPharmacists()
        loadAdminUsers()
    }, [])

    const loadLogs = useCallback(async () => {
        setLoading(true)
        setError('')

        try {
            const response = await AdminService.getPharmacistAssignmentLogs({
                filters,
                ownershipScope,
                page: currentPage,
                pageSize
            })

            if (response.success) {
                setLogs(response.data)
                setTotalPages(response.totalPages || 1)
                setTotalRecords(response.total || 0)
                setCurrentConsultationCount(response.currentConsultationTotal || 0)
                setAssignmentComparison(response.assignmentComparison || null)
            } else {
                setError(response.error || 'Failed to fetch logs')
                setAssignmentComparison(null)
            }
        } catch (err) {
            setError('An unexpected error occurred')
            setAssignmentComparison(null)
        } finally {
            setLoading(false)
        }
    }, [filters, ownershipScope, currentPage])

    useEffect(() => {
        loadLogs()
    }, [loadLogs])

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }))
        setCurrentPage(1)
    }

    const applyCustomDateRange = () => {
        if (customDateFrom && customDateTo) {
            const dateFrom = new Date(customDateFrom)
            dateFrom.setHours(0, 0, 0, 0)

            const dateTo = new Date(customDateTo)
            dateTo.setHours(23, 59, 59, 999)

            setFilters(prev => ({
                ...prev,
                dateRange: { from: dateFrom.toISOString(), to: dateTo.toISOString() }
            }))
            setCurrentPage(1)
            setShowCustomDatePicker(false)
        }
    }

    const clearDateRange = () => {
        setFilters(prev => ({ ...prev, dateRange: null }))
        setCurrentPage(1)
        setCustomDateFrom('')
        setCustomDateTo('')
    }

    const setDatePreset = (preset) => {
        const todayStr = new Date().toISOString().split('T')[0]

        if (preset === 'today') {
            const dateFrom = new Date()
            dateFrom.setHours(0, 0, 0, 0)
            const dateTo = new Date()
            dateTo.setHours(23, 59, 59, 999)
            setFilters(prev => ({ ...prev, dateRange: { from: dateFrom.toISOString(), to: dateTo.toISOString() } }))
            setCustomDateFrom(todayStr)
            setCustomDateTo(todayStr)
        } else if (preset === 'all') {
            clearDateRange()
        }
        setCurrentPage(1)
        setShowCustomDatePicker(false)
    }

    const formatDate = (isoString) => {
        if (!isoString) return '-'
        try {
            const d = new Date(isoString);
            if (isNaN(d.getTime())) return '-'
            return format(d, 'MMM d, yyyy HH:mm')
        } catch (e) {
            return isoString
        }
    }

    // Pagination Handlers
    const goToPage = (page) => {
        setCurrentPage(Math.max(1, Math.min(page, totalPages)))
    }
    const goToNextPage = () => { if (currentPage < totalPages) setCurrentPage(currentPage + 1) }
    const goToPrevPage = () => { if (currentPage > 1) setCurrentPage(currentPage - 1) }

    const hasAssignmentTypeFilter = filters.assignmentType && filters.assignmentType !== 'all'
    const selectedPharmacist = filters.pharmacistUsername && filters.pharmacistUsername !== 'all' ? filters.pharmacistUsername : null

    const handleOwnershipScopeChange = (scope) => {
        setOwnershipScope(scope)
        setCurrentPage(1)
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8" dir="ltr">
            <div className="max-w-7xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate('/admin')} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-500 hover:text-gray-900 border border-gray-200 bg-white">
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div className="h-8 w-px bg-gray-200" />
                        <div className="flex items-center gap-2">
                            <div className="p-2 bg-[#A73A5D]/10 rounded-lg">
                                <FileText className="w-5 h-5 text-[#A73A5D]" />
                            </div>
                            <h2 className="text-xl font-bold text-gray-900">Pharmacist Assignment Logs</h2>
                        </div>
                    </div>

                    <button
                        onClick={loadLogs}
                        disabled={loading}
                        className="flex items-center px-4 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors shadow-sm font-medium text-sm disabled:opacity-70"
                    >
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin text-[#A73A5D]' : 'text-gray-400'}`} />
                        Refresh Logs
                    </button>
                </div>

                {/* Summary Counts */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <button
                        type="button"
                        onClick={() => handleOwnershipScopeChange('all')}
                        className={`text-left bg-white rounded-xl shadow-sm border p-4 flex items-center justify-between gap-4 transition-colors ${ownershipScope === 'all' ? 'border-[#A73A5D] ring-1 ring-[#A73A5D]/20' : 'border-gray-100 hover:border-gray-200'}`}
                    >
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">Current consultations</div>
                            <div className="mt-2 text-3xl font-bold text-gray-900">{currentConsultationCount.toLocaleString()}</div>
                            <div className="mt-1 text-xs text-gray-500">Matches the current owner view from user_consultations.</div>
                        </div>
                        <div className="p-3 rounded-lg bg-emerald-50 text-emerald-700">
                            <User className="w-6 h-6" />
                        </div>
                    </button>

                    <button
                        type="button"
                        onClick={() => handleOwnershipScopeChange('all')}
                        className={`text-left bg-white rounded-xl shadow-sm border p-4 flex items-center justify-between gap-4 transition-colors ${ownershipScope === 'all' ? 'border-[#A73A5D] ring-1 ring-[#A73A5D]/20' : 'border-gray-100 hover:border-gray-200'}`}
                    >
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">Assignment log rows</div>
                            <div className="mt-2 text-3xl font-bold text-gray-900">{totalRecords.toLocaleString()}</div>
                            <div className="mt-1 text-xs text-gray-500">History rows after the current log filters are applied.</div>
                        </div>
                        <div className="p-3 rounded-lg bg-[#A73A5D]/10 text-[#A73A5D]">
                            <FileText className="w-6 h-6" />
                        </div>
                    </button>

                    <button
                        type="button"
                        onClick={() => selectedPharmacist ? handleOwnershipScopeChange('still_with_selected') : null}
                        disabled={!selectedPharmacist}
                        className={`text-left bg-white rounded-xl shadow-sm border p-4 flex items-center justify-between gap-4 transition-colors disabled:cursor-not-allowed ${ownershipScope === 'still_with_selected' ? 'border-[#A73A5D] ring-1 ring-[#A73A5D]/20' : 'border-gray-100 hover:border-gray-200'} ${!selectedPharmacist ? 'opacity-60' : ''}`}
                    >
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">Still with selected pharmacist</div>
                            <div className="mt-2 text-3xl font-bold text-gray-900">{(assignmentComparison?.stillWithSelectedCount || 0).toLocaleString()}</div>
                            <div className="mt-1 text-xs text-gray-500">From filtered log consultations, how many are still owned now.</div>
                        </div>
                        <div className="p-3 rounded-lg bg-sky-50 text-sky-700">
                            <Check className="w-6 h-6" />
                        </div>
                    </button>

                    <button
                        type="button"
                        onClick={() => selectedPharmacist ? handleOwnershipScopeChange('moved_away') : null}
                        disabled={!selectedPharmacist}
                        className={`text-left bg-white rounded-xl shadow-sm border p-4 flex items-center justify-between gap-4 transition-colors disabled:cursor-not-allowed ${ownershipScope === 'moved_away' ? 'border-[#A73A5D] ring-1 ring-[#A73A5D]/20' : 'border-gray-100 hover:border-gray-200'} ${!selectedPharmacist ? 'opacity-60' : ''}`}
                    >
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">Currently with other pharmacist</div>
                            <div className="mt-2 text-3xl font-bold text-gray-900">{(assignmentComparison?.movedAwayCount || 0).toLocaleString()}</div>
                            <div className="mt-1 text-xs text-gray-500">Historical matches that are now assigned to someone else.</div>
                        </div>
                        <div className="p-3 rounded-lg bg-rose-50 text-rose-700">
                            <AlertTriangle className="w-6 h-6" />
                        </div>
                    </button>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900">
                    <div className="font-semibold">Why the two counts can differ</div>
                    <div className="mt-1">
                        Current consultations shows who owns consultations now from user_consultations, while Assignment log rows shows historical assignment activity from pharmacist_assignment_log.
                    </div>
                    <div className="mt-2 text-xs text-amber-800">
                        Tip: click the summary cards above to filter the table to all rows, still-with-selected rows, or moved-away rows.
                    </div>
                    {assignmentComparison && (
                        <div className="mt-1 text-amber-800">
                            In this filter: {assignmentComparison.totalCompared} historical consultations, {assignmentComparison.stillWithSelectedCount} still with {assignmentComparison.selectedPharmacist}, {assignmentComparison.movedAwayCount} moved to another pharmacist{assignmentComparison.unknownCurrentOwnerCount > 0 ? `, ${assignmentComparison.unknownCurrentOwnerCount} with unknown current owner` : ''}.
                        </div>
                    )}
                    {hasAssignmentTypeFilter && (
                        <div className="mt-1 text-amber-800">
                            Assignment Type currently affects only the log rows metric, because assignment_type exists in pharmacist_assignment_log and not in user_consultations.
                        </div>
                    )}
                </div>

                {/* Main Content Area */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col relative">
                    {/* Filters Area */}
                    <div className="p-5 border-b border-gray-100 bg-gray-50/50 rounded-t-xl">

                        {/* Filter Controls */}
                        <div className="flex flex-col gap-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                {/* Search Input */}
                                <div className="relative col-span-1 lg:col-span-2">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search User ID, Name, or Phone..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#A73A5D] focus:border-transparent outline-none"
                                    />
                                </div>

                                {/* Pharmacist Filter */}
                                <div>
                                    <select
                                        value={filters.pharmacistUsername}
                                        onChange={(e) => handleFilterChange('pharmacistUsername', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#A73A5D] focus:border-transparent outline-none bg-white"
                                    >
                                        <option value="all">All Pharmacists</option>
                                        {pharmacists.map(p => (
                                            <option key={p.id} value={p.username}>{p.username}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Pharmacist Type Filter */}
                                <div>
                                    <select
                                        value={filters.pharmacistType}
                                        onChange={(e) => handleFilterChange('pharmacistType', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#A73A5D] focus:border-transparent outline-none bg-white"
                                    >
                                        <option value="all">All PH Types</option>
                                        <option value="full_time">Full-time</option>
                                        <option value="freelance">Freelance</option>
                                    </select>
                                </div>

                                {/* Assignment Type Filter */}
                                <div>
                                    <select
                                        value={filters.assignmentType}
                                        onChange={(e) => handleFilterChange('assignmentType', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#A73A5D] focus:border-transparent outline-none bg-white"
                                    >
                                        <option value="all">All Types</option>
                                        <option value="round_robin">Round Robin</option>
                                        <option value="manual">Manual</option>
                                        <option value="reassignment">Reassignment</option>
                                        <option value="self_assigned">Self Assigned</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                {/* Status Filter */}
                                <div>
                                    <select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#A73A5D] focus:border-transparent outline-none bg-white"
                                    >
                                        <option value="all">All Statuses</option>
                                        <option value="pending">Pending</option>
                                        <option value="wondura_done">Ready (Wondura Done)</option>
                                        <option value="completed">Completed</option>
                                    </select>
                                </div>

                                {/* Outcome Filter */}
                                <div>
                                    <select
                                        value={filters.outcome}
                                        onChange={(e) => handleFilterChange('outcome', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#A73A5D] focus:border-transparent outline-none bg-white"
                                    >
                                        <option value="all">All Outcomes</option>
                                        <option value="paid">Paid</option>
                                        <option value="hot_lead">Hot Lead</option>
                                        <option value="cold_lead">Cold Lead</option>
                                        <option value="not_interested">Not Interested</option>
                                        <option value="no_answer">No Answer</option>
                                    </select>
                                </div>

                                {/* Date Field Filter */}
                                <div>
                                    <select
                                        value={filters.dateField}
                                        onChange={(e) => handleFilterChange('dateField', e.target.value)}
                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-[#A73A5D] focus:border-transparent outline-none bg-white"
                                    >
                                        <option value="assigned_at">Assigned At</option>
                                        <option value="handled_at">Handled At</option>
                                        <option value="completed_at">Completed At</option>
                                        <option value="created_at">Created At</option>
                                    </select>
                                </div>

                                {/* Date Filter Selection */}
                                <div className="relative">
                                    <button
                                        type="button"
                                        onClick={() => setShowCustomDatePicker(!showCustomDatePicker)}
                                        className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#A73A5D]"
                                    >
                                        <span className="flex items-center gap-2 truncate text-gray-600">
                                            <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                            {filters.dateRange ? (
                                                customDateFrom === customDateTo ? `Today` : 'Custom Date'
                                            ) : (
                                                'All Time'
                                            )}
                                        </span>
                                    </button>

                                    {showCustomDatePicker && (
                                        <div className="absolute top-full right-0 mt-1 bg-white border border-gray-200 shadow-xl rounded-lg p-3 z-50 w-64">
                                            <div className="mb-3 space-y-2">
                                                <button onClick={() => setDatePreset('today')} className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 rounded">Today</button>
                                                <button onClick={() => setDatePreset('all')} className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-100 rounded">All Time</button>
                                            </div>
                                            <div className="border-t border-gray-100 pt-3 flex flex-col gap-2">
                                                <div>
                                                    <label className="text-xs text-gray-500 mb-1 block">From</label>
                                                    <input type="date" value={customDateFrom} onChange={e => setCustomDateFrom(e.target.value)} className="w-full text-sm border-gray-300 rounded p-1.5 border" />
                                                </div>
                                                <div>
                                                    <label className="text-xs text-gray-500 mb-1 block">To</label>
                                                    <input type="date" value={customDateTo} onChange={e => setCustomDateTo(e.target.value)} className="w-full text-sm border-gray-300 rounded p-1.5 border" />
                                                </div>
                                                <button
                                                    onClick={applyCustomDateRange}
                                                    className="w-full mt-2 bg-[#A73A5D] text-white py-1.5 rounded text-sm hover:bg-[#8e2e4c] transition-colors"
                                                >Apply</button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Error Banner */}
                    {error && (
                        <div className="bg-red-50 p-3 flex-shrink-0 border-b border-red-100 text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Table Container - Makes it scrollable */}
                    <div className="flex-1 overflow-auto bg-white">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
                                <tr>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">ID / Consult</th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">User Details</th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Pharmacist</th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Assignment</th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Timestamps</th>
                                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">Status & Outcome</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-10 text-center text-gray-500">
                                            <div className="flex justify-center items-center flex-col">
                                                <RefreshCw className="w-8 h-8 text-[#A73A5D] animate-spin mb-2" />
                                                <span>Loading logs...</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : logs.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-10 text-center text-gray-500">
                                            <div className="flex justify-center items-center flex-col">
                                                <FileText className="w-8 h-8 text-gray-300 mb-2" />
                                                <span>No assignment logs found matching your criteria.</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    logs.map((log) => (
                                        <tr key={log.id} className="hover:bg-gray-50 transition-colors">
                                            {/* IDs */}
                                            <td className="px-4 py-3 whitespace-nowrap align-top">
                                                <div className="flex flex-col gap-2 text-sm mt-1">
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="text-gray-400 text-[10px] uppercase font-bold tracking-wider">Log ID</span>
                                                        <CopyableId id={log.id} label="Log ID" />
                                                    </div>
                                                    <div className="flex flex-col gap-0.5">
                                                        <span className="text-gray-400 text-[10px] uppercase font-bold tracking-wider">Consult ID</span>
                                                        <CopyableId id={log.consultation_id} label="Consult ID" />
                                                    </div>
                                                </div>
                                            </td>

                                            {/* User Details */}
                                            <td className="px-4 py-3 align-top min-w-[200px]">
                                                <div className="flex flex-col gap-1.5 text-sm">
                                                    <div className="flex flex-col">
                                                        <span className="font-semibold text-gray-900">{log.users?.name || 'Unknown User'}</span>
                                                        <span className="text-gray-500 text-xs mt-0.5 font-mono">{log.users?.phone || 'No Phone'}</span>
                                                    </div>
                                                    <div className="mt-1.5 pt-1.5 border-t border-gray-100">
                                                        <div className="flex items-center gap-1.5">
                                                            <User className="w-3.5 h-3.5 text-gray-400" />
                                                            <CopyableId id={log.user_id} label="User ID" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Pharmacist Info */}
                                            <td className="px-4 py-3 align-top">
                                                <div className="flex flex-col gap-1.5 text-sm">
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 w-fit">
                                                        {log.pharmacist_username}
                                                    </span>
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold w-fit ${log.pharmacist_type === 'freelance' ? 'bg-purple-100 text-purple-800' : 'bg-sky-100 text-sky-800'}`}>
                                                        {log.pharmacist_type === 'freelance' ? 'Freelance' : log.pharmacist_type === 'full_time' ? 'Full-time' : 'Type unavailable'}
                                                    </span>
                                                    <div className="flex flex-col gap-0.5 text-xs">
                                                        <span className="text-gray-500 uppercase tracking-wider">Current:</span>
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full w-fit font-medium ${log.current_assigned_pharmacist && log.current_assigned_pharmacist.toLowerCase() !== log.pharmacist_username?.toLowerCase()
                                                                ? 'bg-rose-100 text-rose-700'
                                                                : 'bg-emerald-100 text-emerald-700'
                                                            }`}>
                                                            {log.current_assigned_pharmacist || 'Unknown'}
                                                        </span>
                                                        {log.current_assigned_pharmacist && log.current_assigned_pharmacist.toLowerCase() !== log.pharmacist_username?.toLowerCase() && (
                                                            <span className="text-[11px] text-rose-600 font-medium">
                                                                Not {log.pharmacist_username} currently
                                                            </span>
                                                        )}
                                                    </div>
                                                    {log.previous_pharmacist && (
                                                        <div className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                                            <span className="text-[10px] uppercase tracking-wider">Prev:</span>
                                                            <span className="line-through opacity-70">{log.previous_pharmacist}</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Assignment Details */}
                                            <td className="px-4 py-3 align-top whitespace-nowrap">
                                                <div className="flex flex-col gap-1.5 text-sm">
                                                    {(() => {
                                                        const presentation = getAssignmentLogPresentation(log, adminUserMap)
                                                        const badgeClasses = presentation.sourceKind === 'automated'
                                                            ? 'bg-purple-50 text-purple-700 border-purple-200'
                                                            : presentation.isMissingAdminAttribution
                                                                ? 'bg-amber-50 text-amber-700 border-amber-200'
                                                                : 'bg-emerald-50 text-emerald-700 border-emerald-200'

                                                        return (
                                                            <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium w-fit ${badgeClasses}`}>
                                                                {presentation.actionLabel}
                                                            </span>
                                                        )
                                                    })()}
                                                    <AssignedByInfo log={log} adminUserMap={adminUserMap} />
                                                </div>
                                            </td>

                                            {/* Timestamps */}
                                            <td className="px-4 py-3 align-top">
                                                <div className="flex flex-col gap-2 text-xs text-gray-600">
                                                    <div className="flex justify-between gap-4">
                                                        <span className="font-medium text-gray-500 min-w-[60px]">Assigned:</span>
                                                        <span className="text-gray-900 whitespace-nowrap">{formatDate(log.assigned_at)}</span>
                                                    </div>
                                                    {log.handled_at && (
                                                        <div className="flex justify-between gap-4">
                                                            <span className="font-medium text-gray-500 min-w-[60px]">Handled:</span>
                                                            <span className="text-gray-900 whitespace-nowrap">{formatDate(log.handled_at)}</span>
                                                        </div>
                                                    )}
                                                    {log.completed_at && (
                                                        <div className="flex justify-between gap-4">
                                                            <span className="font-medium text-gray-500 min-w-[60px]">Completed:</span>
                                                            <span className="text-gray-900 whitespace-nowrap">{formatDate(log.completed_at)}</span>
                                                        </div>
                                                    )}
                                                    {log.backlog_days > 0 && (
                                                        <span className="mt-1 inline-flex items-center text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded w-fit text-[10px] font-medium border border-orange-200">
                                                            <Clock className="w-3 h-3 mr-1" />
                                                            {log.backlog_days} days backlog
                                                        </span>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Status & Outcome */}
                                            <td className="px-4 py-3 align-top text-sm">
                                                <div className="flex flex-col gap-2">
                                                    {log.current_status && (
                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 w-fit capitalize">
                                                            {log.current_status.replace('_', ' ')}
                                                        </span>
                                                    )}
                                                    {log.final_outcome && (
                                                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium w-fit capitalize
                            ${log.final_outcome === 'paid' ? 'bg-green-100 text-green-800' :
                                                                log.final_outcome === 'hot_lead' ? 'bg-orange-100 text-orange-800' :
                                                                    'bg-gray-100 text-gray-600'}`
                                                        }>
                                                            {log.final_outcome.replace('_', ' ')}
                                                        </span>
                                                    )}
                                                    {log.canonical_status_source === 'user_consultations' && (
                                                        <span className="text-[10px] text-blue-600 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5 w-fit">
                                                            canonical: user_consultations
                                                        </span>
                                                    )}
                                                    {log.notes && (
                                                        <p className="text-xs text-gray-500 italic mt-1 max-w-[150px] truncate" title={log.notes}>
                                                            {log.notes}
                                                        </p>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer Pagination */}
                    <div className="p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0 flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                            Showing <span className="font-medium">{(currentPage - 1) * pageSize + (logs.length > 0 ? 1 : 0)}</span> to <span className="font-medium">{Math.min(currentPage * pageSize, totalRecords)}</span> of <span className="font-medium">{totalRecords}</span> log entries
                        </div>

                        {totalPages > 1 && (
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={goToPrevPage}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
                                >
                                    <ChevronLeft className="w-4 h-4 mr-1" />
                                    Prev
                                </button>
                                <div className="flex -space-x-px rounded-md shadow-sm">
                                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                        let pageNum
                                        if (totalPages <= 5) {
                                            pageNum = i + 1
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1
                                        } else if (currentPage >= totalPages - 2) {
                                            pageNum = totalPages - 4 + i
                                        } else {
                                            pageNum = currentPage - 2 + i
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => goToPage(pageNum)}
                                                className={`relative inline-flex items-center px-4 py-1.5 text-sm font-medium border ${currentPage === pageNum
                                                    ? 'z-10 bg-[#A73A5D] text-white border-[#A73A5D]'
                                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        )
                                    })}
                                </div>
                                <button
                                    onClick={goToNextPage}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1.5 text-sm font-medium border border-gray-300 rounded-md bg-white hover:bg-gray-50 text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
                                >
                                    Next
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PharmacistAssignmentLogs
